import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactElement, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PasswordField } from '../../../components/PasswordField';
import { TextLink } from '../../../components/TextLink';
import { apiErrorMessageKey } from '../../../lib/api';
import { useLoginMutation, useRequestMagicLinkMutation } from '../../../lib/auth';
import { toInternalLocation } from '../../../lib/internalLocation';
import { rememberPendingRedirect } from '../../../lib/pendingRedirect';
import { isValidEmail } from '../../../lib/validators';

type Method = 'link' | 'password';

export function LoginPage(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { redirect, prefillEmail, reason } = useSearch({ from: '/split-hero-layout/login' });
    const login = useLoginMutation();
    const requestMagicLink = useRequestMagicLinkMutation();
    const [method, setMethod] = useState<Method>('link');
    // Derived, not snapshotted: the address can arrive after this mounts, when a redirect adds it to the search
    // of a route that is already rendered.
    const [editedEmail, setEditedEmail] = useState<string>();
    const email = editedEmail ?? prefillEmail ?? '';
    const [password, setPassword] = useState('');

    const emailValid = isValidEmail(email);
    const canSubmit = method === 'link' ? emailValid : emailValid && password.length > 0;
    const isPending = method === 'link' ? requestMagicLink.isPending : login.isPending;

    const activeError = method === 'link' ? requestMagicLink.error : login.error;
    const errorMessage = activeError ? t(apiErrorMessageKey(activeError, 'ERRORS.LOGIN_FAILED')) : undefined;

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        if (reason) {
            void navigate({ to: '/login', search: { redirect, prefillEmail }, replace: true });
        }
        if (method === 'link') {
            // Kept in this browser rather than sent with the request: a target the backend put into the mail
            // would let anyone have Houzy send a victim a genuine link pointing wherever they chose.
            rememberPendingRedirect(redirect);
            requestMagicLink.mutate(
                { email },
                { onSuccess: () => void navigate({ to: '/find-account/check-email', search: { email } }) },
            );
            return;
        }
        login.mutate({ email, password }, { onSuccess: () => void navigate(toInternalLocation(redirect)) });
    };

    return (
        <Stack spacing={4}>
            <Box>
                <Tabs
                    value={method}
                    onChange={(_, value) => {
                        if (value === 'link' || value === 'password') {
                            setMethod(value);
                        }
                    }}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none' } }}>
                    <Tab value="link" label={t('AUTH.LOGIN.TAB_LINK')} />
                    <Tab value="password" label={t('AUTH.LOGIN.TAB_PASSWORD')} />
                </Tabs>
            </Box>

            <Stack spacing={1}>
                <Typography variant="h5" component="h1">
                    {t('AUTH.LOGIN.TITLE')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <Trans
                        i18nKey="AUTH.LOGIN.NEW_PROMPT"
                        components={{
                            registerLink: <TextLink to="/register-company" color="primary" />,
                        }}
                    />
                </Typography>
            </Stack>

            <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit}>
                {errorMessage ? (
                    <Alert severity="error">{errorMessage}</Alert>
                ) : (
                    reason === 'no-cockpit-access' && (
                        <Alert severity="warning">{t('AUTH.LOGIN.NO_COCKPIT_ACCESS')}</Alert>
                    )
                )}
                <TextField
                    label={t('AUTH.LOGIN.FIELDS.EMAIL')}
                    type="email"
                    fullWidth
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEditedEmail(e.target.value)}
                />
                {method === 'password' && (
                    <>
                        <PasswordField
                            label={t('AUTH.LOGIN.FIELDS.PASSWORD')}
                            fullWidth
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <TextLink to="/forgot-password" variant="body2" color="primary" sx={{ alignSelf: 'flex-end' }}>
                            {t('AUTH.LOGIN.FORGOT_PASSWORD')}
                        </TextLink>
                    </>
                )}
                <Button type="submit" variant="contained" fullWidth disabled={!canSubmit || isPending}>
                    {method === 'link' ? t('AUTH.LOGIN.SUBMIT_LINK') : t('AUTH.LOGIN.SUBMIT_PASSWORD')}
                </Button>
                {method === 'link' && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {t('AUTH.LOGIN.NO_PASSWORD_HINT')}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}
